import React from 'react';
import {
  Input,
  NumberInput,
  Select,
  SelectItem,
  Switch,
  Textarea,
} from '@heroui/react';
import { RequiredInfo, RequiredInfoValue } from '@/util/types/serverTypes';
import DateSelectorNextUI from '../forms/DateSelectorNextUI';

interface RequiredInfoResolverProps {
  requiredInfo: RequiredInfo;
  onChange: (value: RequiredInfoValue) => void;
  value: string | null;
}

export default function RequiredInfoResolver({
  requiredInfo,
  onChange,
  value,
}: RequiredInfoResolverProps) {
  // Handle value changes and notify parent
  const handleValueChange = (newValue: string | null) => {
    onChange({
      _requiredInfoId: requiredInfo._id,
      name: requiredInfo.name,
      value: newValue || '',
    });
  };

  // Render different input based on requiredInfo type
  switch (requiredInfo.type) {
    case 'text':
      return (
        <Textarea
          label={requiredInfo.name}
          placeholder={`Enter ${requiredInfo.name.toLowerCase()}`}
          value={value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          variant='bordered'
          className='my-1 w-full'
          size='sm'
          isRequired={requiredInfo.required}
          isInvalid={requiredInfo.required && !value}
          classNames={{
            inputWrapper: 'bg-white border shadow-none',
            label: 'font-light uppercase text-xs',
          }}
          minRows={1}
        />
      );

    case 'number':
      return (
        <NumberInput
          label={requiredInfo.name}
          placeholder={`Enter ${requiredInfo.name.toLowerCase()}`}
          value={value ? Number(value) : undefined}
          onChange={(e) => {
            // Convert number to string for storage
            const val = String(+e);
            handleValueChange(val);
          }}
          size='sm'
          variant='bordered'
          className='my-1 w-full'
          isRequired={requiredInfo.required}
          isInvalid={requiredInfo.required && !value}
          classNames={{
            inputWrapper: 'bg-white border shadow-none',
            label: 'font-light uppercase text-xs',
          }}
        />
      );

    case 'select':
      // Ensure we have options to display
      const options = requiredInfo.options || [];

      return (
        <Select
          label={requiredInfo.name}
          placeholder={`Select ${requiredInfo.name.toLowerCase()}`}
          selectedKeys={value ? [value] : []}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys as Set<string>)[0];
            handleValueChange(selected || null);
          }}
          // Explicit clear affordance so the user can blank the selection regardless of
          // option count — including single-option lists where toggling off a listbox item
          // is not discoverable (ATT-1335).
          onClear={value ? () => handleValueChange(null) : undefined}
          isClearable={true}
          size='sm'
          variant='bordered'
          className='my-1 w-full'
          isRequired={requiredInfo.required}
          isInvalid={requiredInfo.required && !value}
          classNames={{
            trigger: 'bg-white border shadow-none',
            label: 'font-light uppercase text-xs',
          }}
          listboxProps={{
            itemClasses: {
              title: 'whitespace-normal',
            },
          }}
          selectionMode='single'
          disallowEmptySelection={false}
        >
          {options.map((option) => (
            <SelectItem key={option}>{option}</SelectItem>
          ))}
        </Select>
      );

    case 'date':
      return (
        <DateSelectorNextUI
          label={requiredInfo.name}
          value={value || undefined}
          onChange={(date) => handleValueChange(date)}
          size='sm'
          isInvalid={requiredInfo.required && !value}
          withTime={requiredInfo.includeTime || false}
        />
      );

    case 'boolean':
      return (
        <div
          className={`my-1 rounded-lg border bg-white px-3 py-2 hover:border-gray-600 ${
            requiredInfo.required && 'border-red-500'
          }`}
        >
          <Switch
            isSelected={value === 'true'}
            onChange={(e) => handleValueChange(e.target.checked.toString())}
            classNames={{
              label: 'text-sm font-bold uppercase',
            }}
          >
            {requiredInfo.name}
          </Switch>
        </div>
      );

    case 'indicator':
      // Indicator type with N/A, Yes, No options
      const indicatorOptions = [
        { value: 'n/a', label: 'N/A' },
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ];

      return (
        <Select
          label={requiredInfo.name}
          placeholder={`Select ${requiredInfo.name.toLowerCase()}`}
          selectedKeys={value ? [value] : []}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys as Set<string>)[0];
            handleValueChange(selected || null);
          }}
          onClear={value ? () => handleValueChange(null) : undefined}
          isClearable={true}
          size='sm'
          variant='bordered'
          className='my-1 w-full'
          isRequired={requiredInfo.required}
          isInvalid={requiredInfo.required && !value}
          classNames={{
            trigger: 'bg-white border shadow-none',
            label: 'font-light uppercase text-xs',
          }}
          listboxProps={{
            itemClasses: {
              title: 'whitespace-normal',
            },
          }}
          selectionMode='single'
          disallowEmptySelection={false}
        >
          {indicatorOptions.map((option) => (
            <SelectItem key={option.value}>{option.label}</SelectItem>
          ))}
        </Select>
      );

    default:
      return null;
  }
}
